import React from "react";
import {Link, useLocation} from "react-router-dom";
import {
  ShoppingCart,
  Menu,
  X,
  Facebook,
  Twitter,
  Instagram,
} from "lucide-react";
import {useCart} from "../contexts/CartContext";
import {useSiteOptions} from "../contexts/SiteOptionsContext";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import threadsWhite from "../assets/threads-white.png";
import goodreadsWhite from "../assets/goodreads-white.png";

const Layout: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = React.useState(false);
  const {state} = useCart();
  const {ecommerceEnabled, loading: optionsLoading} = useSiteOptions();
  const location = useLocation();

  const navigation = [
    {name: "Home", href: "/"},
    {name: "About", href: "/about"},
    {name: "Projects", href: "/projects"},
    {name: "Shop", href: "/shop"},
  ];

  const cartItemCount = state.items.reduce(
    (total, item) => total + item.quantity,
    0
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Left: mobile hamburger + desktop links */}
            <div className="flex items-center">
              {/* Mobile hamburger on the left */}
              <div className="md:hidden flex items-center mr-3">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-gray-500 hover:text-gray-900"
                  aria-label="Toggle Navigation"
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>

              {/* Desktop links shifted left with animated underline */}
              <div className="hidden md:flex items-center space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`relative text-sm font-medium transition-colors after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-primary-600 after:transition-all after:duration-300 hover:after:w-full ${
                      location.pathname === item.href
                        ? "text-primary-600 after:w-full"
                        : "text-gray-500 hover:text-gray-900"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Right: cart icon */}
            {!optionsLoading && ecommerceEnabled && (
              <div className="flex items-center">
                <Link
                  to="/shop"
                  className="relative text-gray-500 hover:text-gray-900 transition-colors"
                  aria-label="Open cart"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {cartItemCount}
                    </span>
                  )}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile slideout menu */}
        <div
          className={`fixed inset-0 z-40 md:hidden transition-opacity duration-300 ${
            mobileMenuOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className={`absolute left-0 top-16 w-72 max-w-[80vw] bg-white border-r shadow-lg rounded-r-md transition-transform duration-300 ${
              mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 pt-4 pb-6 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium rounded-md ${
                    location.pathname === item.href
                      ? "text-primary-600 bg-primary-50"
                      : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-0 sm:px-0 lg:px-8 pt-8 min-h-screen">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-primary-200 border-t">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            {/* Left side - Copyright and links */}
            <div className="space-y-2">
              <p className="text-base text-white">
                © {new Date().getFullYear()} Hallie Parker Writes. All rights
                reserved.
              </p>
              <button
                onClick={() => setPrivacyModalOpen(true)}
                className="text-base text-white hover:text-primary-100 underline"
              >
                Privacy Policy
              </button>
              <p className="text-base text-white">
                Website design and development by{" "}
                <a
                  href="https://wademedia.biz/"
                  target="_blank"
                  rel="noopener"
                  title="Wade Media — web design and development"
                  className="text-white underline hover:text-primary-100 transition-colors"
                >
                  Wade Media
                </a>
              </p>
            </div>

            {/* Right side - Social media */}
            <div className="flex flex-col items-start md:items-end space-y-3">
              <h4 className="text-base font-bold text-xl text-white">Follow</h4>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/hallieparkerwrites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-primary-100 transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a
                  href="https://www.tiktok.com/@hallieparkerwrites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-primary-100 transition-colors"
                  aria-label="TikTok"
                >
                  <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </a>
                <a
                  href="https://www.threads.com/@hallieparkerwrites"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-primary-100 transition-colors"
                  aria-label="Threads"
                >
                  <img src={threadsWhite} alt="Threads" className="h-6 w-6" />
                </a>
                <a
                  href="https://www.goodreads.com/author/show/23130527.Hallie_Parker"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-primary-100 transition-colors"
                  aria-label="Goodreads"
                >
                  <img
                    src={goodreadsWhite}
                    alt="Goodreads"
                    className="h-6 w-6"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
      />
    </div>
  );
};

export default Layout;
